import { chaveDia, formatarDataCurta, formatarHora, rotuloDiaLongo } from "@/shared/lib/datas";
import { CORES_MODULO } from "@/modules/calendario/cores";
import type { ItemCalendario } from "@/modules/calendario/services/calendarioService";

interface AgendaCalendarioProps {
  itens: ItemCalendario[];
  vazio?: string;
  /** Se informado, cada item vira clicável e abre o detalhe (Sprint 3). */
  onSelecionar?: (item: ItemCalendario) => void;
}

/** Lista de itens agrupada por dia (usada na tela do Calendário e no preview). */
export function AgendaCalendario({ itens, vazio = "Nada agendado por aqui.", onSelecionar }: AgendaCalendarioProps) {
  if (!itens.length) {
    return <p className="px-1 py-8 text-center text-sm text-muted">{vazio}</p>;
  }

  const grupos: { chave: string; rotulo: string; itens: ItemCalendario[] }[] = [];
  const indicePorChave = new Map<string, number>();
  for (const it of itens) {
    const chave = chaveDia(it.inicio);
    const indice = indicePorChave.get(chave);
    if (indice === undefined) {
      indicePorChave.set(chave, grupos.length);
      grupos.push({ chave, rotulo: rotuloDiaLongo(it.inicio), itens: [it] });
    } else {
      grupos[indice]!.itens.push(it);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      {grupos.map((g) => (
        <div key={g.chave}>
          <p className="mb-2 px-1 text-xs font-bold uppercase tracking-wide text-muted">{g.rotulo}</p>
          <div className="flex flex-col gap-2">
            {g.itens.map((it) => {
              const cor = CORES_MODULO[it.modulo];
              // F4-17: evento de mais de um dia aparece uma vez só (no dia em
              // que começa, pra não repetir o mesmo item em vários grupos da
              // lista) — mas com "até DD mmm" no lugar da hora, deixando
              // claro que ele continua nos dias seguintes.
              const multiDia = !!it.fim && chaveDia(it.fim) !== chaveDia(it.inicio);
              const clicavel = !!onSelecionar;
              const conteudo = (
                <>
                  <span className={`w-1.5 shrink-0 rounded-full ${cor.barra}`} />
                  <div className="w-14 shrink-0 text-sm font-bold text-text">
                    {multiDia ? `até ${formatarDataCurta(it.fim!)}` : it.diaInteiro ? "dia" : formatarHora(it.inicio)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-text">{it.titulo}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
                      {it.local && <span>{it.local}</span>}
                      {it.ministerio && <span>· {it.ministerio}</span>}
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${cor.tag}`}>
                        {cor.rotulo}
                      </span>
                      {it.status === "pendente" && (
                        <span className="rounded bg-warning/15 px-1.5 py-0.5 text-[10px] font-bold text-warning">
                          Pendente
                        </span>
                      )}
                      {it.visibilidade === "privado" && (
                        <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] font-bold text-muted">
                          Privado
                        </span>
                      )}
                    </p>
                  </div>
                </>
              );
              return clicavel ? (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onSelecionar!(it)}
                  className="flex w-full items-stretch gap-3 rounded-xl border border-border bg-surface p-3 text-left shadow-sm transition-colors hover:bg-surface-2"
                >
                  {conteudo}
                </button>
              ) : (
                <div
                  key={it.id}
                  className="flex items-stretch gap-3 rounded-xl border border-border bg-surface p-3 shadow-sm"
                >
                  {conteudo}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
